// The row of saved filter presets shown directly under the calendar's main filter line.
// Each chip is a named snapshot of the tenant + doctor (+ standalone calendar) selection
// and the day-view "Group" toggle: click (or press Alt/⌥ + its 1–9 number key) to apply it,
// drag to reorder, double-click the name to rename, hover the × to delete. A "Save" popover
// captures the current selection as a new preset (up to nine).
//
// The chips own only presentation + local edit state; every mutation flows back to the
// parent (Calendar) through callbacks, which delegate to saved-calendar-filters-store.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BookmarkPlus, Check, ChevronDown, ChevronRight, Star, X } from "lucide-react";
import { cn, ALT_KEY_LABEL } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MAX_SAVED_CALENDAR_FILTERS } from "@/lib/stores/saved-calendar-filters-store";

// Order-independent equality for two id lists — used to flag the preset matching the
// current selection so it can be highlighted as active.
function sameIdSet(left, right) {
  const a = Array.isArray(left) ? left : [];
  const b = Array.isArray(right) ? right : [];
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

// A short, human-readable description of what a preset selects, used as the chip's tooltip.
// Names up to two entities, otherwise falls back to a count (or "all" / "none").
function describeSelection(ids, options, noun) {
  const list = Array.isArray(ids) ? ids : [];
  if (list.length === 0) {
    return `no ${noun}s`;
  }
  if (options.length > 0 && list.length === options.length) {
    return `all ${noun}s`;
  }
  const nameById = new Map(options.map((option) => [option.id, option.name]));
  const named = list.map((id) => nameById.get(id)).filter(Boolean);
  if (named.length > 0 && named.length <= 2) {
    return named.join(", ");
  }
  return `${list.length} ${noun}s`;
}

// The day-view "Group" toggle uses "Clinic"/"Doctor" labels ("tenant" is the stored id).
function describeGroupBy(groupBy) {
  return groupBy === "doctor" ? "doctor" : "clinic";
}

// A preset saved before the Calendars facet existed carries `calendarIds: null`, which
// applies as "every standalone calendar" — so for matching/summaries it resolves to the
// full option list. The facet only counts at all when there are calendars to pick from.
function resolvedCalendarIds(filter, calendarOptions) {
  return Array.isArray(filter?.calendarIds)
    ? filter.calendarIds
    : calendarOptions.map((option) => option.id);
}

function summarizeFilter(filter, tenantOptions, doctorOptions, calendarOptions = []) {
  const parts = [
    `Tenants: ${describeSelection(filter.tenantIds, tenantOptions, "tenant")}`,
    `Doctors: ${describeSelection(filter.doctorIds, doctorOptions, "doctor")}`,
  ];
  if (calendarOptions.length > 0) {
    parts.push(
      `Calendars: ${describeSelection(resolvedCalendarIds(filter, calendarOptions), calendarOptions, "calendar")}`,
    );
  }
  parts.push(`Grouped by ${describeGroupBy(filter.groupBy)}`);
  return parts.join(" · ");
}

// One draggable preset chip: number badge (its Alt/⌥+1–9 shortcut), name, and a hover-reveal
// delete. Double-clicking the name opens an inline rename. Drag listeners cover the whole
// chip (distance-activated, so a plain click still applies); they detach while renaming so
// the text field stays interactive.
function SavedFilterChip({ filter, index, active, summary, onApply, onDelete, onRename }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: filter.id,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(filter.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const beginEdit = () => {
    setDraftName(filter.name);
    setIsEditing(true);
  };
  const commitEdit = () => {
    setIsEditing(false);
    const next = draftName.trim();
    if (next && next !== filter.name) {
      onRename(filter.id, next);
    }
  };
  const cancelEdit = () => {
    setIsEditing(false);
    setDraftName(filter.name);
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group/chip relative inline-flex h-7 select-none items-center gap-1 rounded-lg border pl-1 pr-1 text-[12px] font-medium transition-colors",
        isDragging ? "z-10 opacity-70 shadow-md ring-2 ring-ring/50" : "",
        active
          ? "border-primary/50 bg-primary/10 text-foreground"
          : "border-border bg-card text-foreground hover:bg-muted",
      )}
      title={summary}
    >
      {/* The number badge is the drag handle (pointer + keyboard sortable) AND a click-to-
          apply shortcut. Keeping the handle on this leaf button — rather than spreading the
          sortable role=button/tabIndex onto the <li> — avoids a focusable wrapper nesting
          the apply/delete buttons. */}
      <button
        type="button"
        {...attributes}
        {...(isEditing ? {} : listeners)}
        onClick={onApply}
        aria-label={`Saved filter ${index + 1}, ${filter.name} — drag to reorder or activate to apply`}
        title={`Apply (press ${ALT_KEY_LABEL}${index + 1}) · drag to reorder`}
        className={cn(
          "inline-flex size-5 shrink-0 cursor-grab items-center justify-center rounded-md text-[11px] font-bold tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring active:cursor-grabbing",
          active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {index + 1}
      </button>

      {isEditing ? (
        <input
          ref={inputRef}
          value={draftName}
          onChange={(event) => setDraftName(event.target.value.slice(0, 40))}
          onBlur={commitEdit}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              event.preventDefault();
              commitEdit();
            } else if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={`Rename ${filter.name}`}
          className="h-5 w-28 rounded border border-input bg-background px-1.5 text-[12px] font-medium text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      ) : (
        <button
          type="button"
          // Single click applies; double-click renames. Skip the apply on the first click of
          // a double-click (event.detail === 2) so renaming doesn't also re-apply the filter.
          onClick={(event) => {
            if (event.detail > 1) {
              return;
            }
            onApply();
          }}
          onDoubleClick={beginEdit}
          className="max-w-[10rem] truncate rounded px-1 text-left outline-none focus-visible:ring-1 focus-visible:ring-ring"
          title={`Apply "${filter.name}" (press ${ALT_KEY_LABEL}${index + 1}) · double-click to rename`}
        >
          {filter.name}
        </button>
      )}

      {!isEditing ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(filter.id);
          }}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={`Delete saved filter ${filter.name}`}
          title="Delete saved filter"
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground/60 opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover/chip:opacity-100"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </li>
  );
}

// The save popover: a tiny named-capture form prefilled with a sensible default. Submitting
// adds the preset; the popover closes itself.
function SaveFilterButton({ defaultName, disabled, atLimit, onSave }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const inputRef = useRef(null);

  const handleOpenChange = (next) => {
    if (next) {
      setName(defaultName);
    }
    setOpen(next);
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    onSave(trimmed);
    setOpen(false);
  };

  if (atLimit) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg px-2 text-[11px] font-medium text-muted-foreground">
        <Star className="size-3.5" />
        All {MAX_SAVED_CALENDAR_FILTERS} slots used
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title="Save the current clinic, doctor & calendar selection and grouping as a preset"
          className={cn(
            "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed border-border bg-card px-2.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            disabled && "cursor-not-allowed opacity-50 hover:bg-card hover:text-muted-foreground",
          )}
        >
          <BookmarkPlus className="size-3.5" />
          Save
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <p className="mb-1.5 text-xs font-semibold text-foreground">Save current filter</p>
        <p className="mb-2.5 text-[11px] leading-relaxed text-muted-foreground">
          Saves the selected tenants, doctors, calendars &amp; day-view grouping. Recall it from
          this bar or by pressing {ALT_KEY_LABEL === "⌥" ? "⌥ (Option)" : "Alt"} + its number key.
        </p>
        <div className="flex items-center gap-1.5">
          <input
            ref={inputRef}
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value.slice(0, 40))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Filter name"
            aria-label="Saved filter name"
            className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-[12px] font-medium text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!name.trim()}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            <Check className="size-3.5" />
            Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function SavedFiltersBar({
  filters = [],
  currentTenantIds = [],
  currentDoctorIds = [],
  /** Selected standalone calendar ids (null while the facet hasn't resolved yet). */
  currentCalendarIds = null,
  currentGroupBy = "tenant",
  tenantOptions = [],
  doctorOptions = [],
  /** Standalone calendar options; the calendar facet is ignored while this is empty. */
  calendarOptions = [],
  expanded = true,
  canSave = false,
  onToggleExpanded,
  onApply,
  onSave,
  onDelete,
  onRename,
  onReorder,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => filters.map((filter) => filter.id), [filters]);
  const activeId = useMemo(() => {
    const match = filters.find(
      (filter) =>
        sameIdSet(filter.tenantIds, currentTenantIds) &&
        sameIdSet(filter.doctorIds, currentDoctorIds) &&
        (calendarOptions.length === 0 ||
          sameIdSet(resolvedCalendarIds(filter, calendarOptions), currentCalendarIds || [])) &&
        filter.groupBy === currentGroupBy,
    );
    return match ? match.id : null;
  }, [filters, currentTenantIds, currentDoctorIds, currentCalendarIds, calendarOptions, currentGroupBy]);

  const atLimit = filters.length >= MAX_SAVED_CALENDAR_FILTERS;
  // Prefill a non-colliding "Filter N": the lowest N ≥ 1 whose name isn't already taken
  // (so deleting #2 then saving again doesn't re-propose a name that already exists).
  const defaultName = useMemo(() => {
    const taken = new Set(filters.map((filter) => filter.name));
    let n = 1;
    while (taken.has(`Filter ${n}`)) {
      n += 1;
    }
    return `Filter ${n}`;
  }, [filters]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(active.id, over.id);
    }
  };

  return (
    <div className="flex flex-none items-center gap-2 border-b border-border bg-background/95 px-6 py-1.5 backdrop-blur">
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        title={expanded ? "Hide saved filters" : "Show saved filters"}
        className="inline-flex h-7 shrink-0 items-center gap-1 rounded-lg px-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        <Star className="size-3.5" />
        <span>Saved</span>
        {filters.length > 0 ? (
          <span className="rounded bg-muted-foreground/10 px-1 tabular-nums text-muted-foreground">
            {filters.length}
          </span>
        ) : null}
      </button>

      {expanded ? (
        <>
          {/* Chips scroll horizontally when they overflow; the Save affordance stays pinned. */}
          <ScrollArea orientation="horizontal" className="min-w-0 flex-1">
            <div className="flex items-center">
            {filters.length > 0 ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
                  <ul className="flex items-center gap-1.5">
                    {filters.map((filter, index) => (
                      <SavedFilterChip
                        key={filter.id}
                        filter={filter}
                        index={index}
                        active={filter.id === activeId}
                        summary={summarizeFilter(filter, tenantOptions, doctorOptions, calendarOptions)}
                        onApply={() => onApply(filter)}
                        onDelete={onDelete}
                        onRename={onRename}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            ) : (
              <span className="truncate text-[11.5px] text-muted-foreground">
                Save the current tenant, doctor &amp; calendar selection and grouping to recall it instantly with {ALT_KEY_LABEL}1–{ALT_KEY_LABEL}9.
              </span>
            )}
            </div>
          </ScrollArea>

          <div className="shrink-0">
            <SaveFilterButton
              defaultName={defaultName}
              disabled={!canSave}
              atLimit={atLimit}
              onSave={onSave}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
